import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'oneok/environments/environment';
import { ToastService, SpinnerService, StorageService } from '@core/services';
import { AuthService } from '@customer/services';
import { Account } from '@legacy-models/portal/Account';
import { Contact } from '@legacy-models/portal/Contact';
import { SessionAuth } from '@legacy-models/auth/user';
import { Envelope } from '@legacy-models/docu-sign/ds-envelope';
import { EnvelopeResponse } from '@legacy-models/docu-sign/ds-envelope-response';
import { IDocuSignAuthenticate } from '@legacy-models/docu-sign/docusign-auth';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DocuSign {
  token = ';';
  acct: Account;
  contact: Contact;
  baseURL = environment.apiUrlV2;
  private readonly _httpClient: HttpClient = inject(HttpClient);
  private readonly _toastSvc = inject(ToastService);
  private readonly _authSvc: AuthService = inject(AuthService);
  readonly spinnerSvc: SpinnerService = inject(SpinnerService);
  readonly storageSvc: StorageService = inject(StorageService);

  constructor() {
    this.storageSvc.currentAccount$.subscribe(currAccount => {
      if (currAccount) {
        this.acct = currAccount;
      }
    });
  }

  //Post to authenticate against DocuSign's API and retrieve DS Access Token
  postDocuSignAuthenticate(accountId: string): Observable<any> {
    return this._httpClient.post<IDocuSignAuthenticate>(
      `${this.baseURL}/docusign/accounts/${accountId}/authenticate`,
      ''
    );
  }

  //Post to create docusign envelope for a given user : TODO Fix error... need to probably continue to subscribe from this like we've always done.
  postDocuSignEnvelope(envelope: Envelope, accountId: string): Observable<any> {
    const dsAccessToken = this.getAccessTokenFromCookie();
    const headers = { DSAccessToken: dsAccessToken };
    return this._httpClient.post<EnvelopeResponse>(
      `${this.baseURL}/docusign/accounts/${accountId}/envelopes`,
      envelope,
      { headers }
    );
  }

  //Retrieve DSAccess Token
  getAccessTokenFromCookie(): string {
    const findtoken = document.cookie.split('; ').find(row => row.startsWith('DSAccessToken'));

    if (findtoken === undefined) {
      return '';
    } else {
      return findtoken.split('=')[1];
    }
  }

  setDocuSignAccessToken(result: IDocuSignAuthenticate): void {
    const date = new Date();
    date.setTime(date.getTime() + 30 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();
    document.cookie = 'DSAccessToken=' + result.AccessToken + ';' + expires;
  }

  mapRecipientContactObjectIntoEnvelope(session: SessionAuth): Envelope {
    const contact: Contact = session.Contact ?? {};

    return {
      SignerName: contact.FullName ?? `${contact.FirstName ?? ''} ${contact.LastName ?? ''}`.trim(),
      SignerEmail: contact.Email ?? '',
      ContactId: contact.Id ?? '',
      TemplateId: this._authSvc.dTest(environment.dsConfig.TemplateId),
      BasePath: environment.dsConfig.BasePath,
      AccountId: this._authSvc.dTest(environment.dsConfig.AccountId)
    };
  }

  isDsAccessTokenValid(token: string): boolean {
    const expiry = JSON.parse(atob(token.split('.')[1])).exp;
    return Math.floor(new Date().getTime() / 1000) >= expiry;
  }
}
